/**
 * RecoveryPhraseStep Component
 *
 * Step 3 of enrollment: Generate and display recovery phrase.
 * This step is SKIPPABLE but recommended.
 *
 * FERP-PII-052: BIP39 recovery phrase for emergency key recovery.
 *
 * SECURITY NOTES:
 * - Recovery phrase is shown ONCE and never stored
 * - User must confirm they wrote it down
 * - Without phrase + biometric loss = data unrecoverable
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Checkbox,
  Paper,
  SimpleGrid,
  CopyButton,
  ActionIcon,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconKey,
  IconAlertTriangle,
  IconCopy,
  IconCheck,
  IconEye,
  IconEyeOff,
} from '@tabler/icons-react';

export interface RecoveryPhraseStepProps {
  /** Called when step is complete */
  onComplete: (credentialId?: string, prfOutput?: string, deviceName?: string) => void;

  /** Called when user skips this step */
  onSkip: () => void;

  /** Loading state from parent */
  loading: boolean;
}

/**
 * Generate a random 12-word recovery phrase (BIP39-style)
 * In production, this would come from a proper BIP39 implementation
 */
function generateRecoveryPhrase(): string[] {
  // Subset of BIP39 words for demo (production would use full wordlist)
  const words = [
    'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
    'academy', 'access', 'accident', 'account', 'accuse', 'achieve', 'acid', 'acoustic',
    'acquire', 'across', 'action', 'actor', 'actress', 'actual', 'adapt', 'address',
    'adjust', 'admit', 'adult', 'advance', 'advice', 'aerobic', 'affair', 'afford',
    'afraid', 'again', 'agent', 'agree', 'ahead', 'aim', 'air', 'airport',
    'aisle', 'alarm', 'album', 'alcohol', 'alert', 'alien', 'allow', 'almost',
  ];

  const phrase: string[] = [];
  for (let i = 0; i < 12; i++) {
    const randomIndex = Math.floor(Math.random() * words.length);
    const word = words[randomIndex];
    if (word !== undefined) {
      phrase.push(word);
    }
  }
  return phrase;
}

export const RecoveryPhraseStep: React.FC<RecoveryPhraseStepProps> = ({
  onComplete,
  onSkip,
  loading,
}) => {
  const [phrase, setPhrase] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [showPhrase, setShowPhrase] = useState(false);

  // Generate phrase on mount
  useEffect(() => {
    setPhrase(generateRecoveryPhrase());
  }, []);

  /**
   * Handle completion (user confirmed they saved the phrase)
   */
  const handleComplete = () => {
    // In production, we'd hash the phrase and send to backend
    // for future recovery verification
    onComplete();
  };

  const phraseText = phrase.join(' ');

  return (
    <Stack gap="md" mt="md">
      {/* Warning about recovery phrase */}
      <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Important">
        <Text size="sm">
          This recovery phrase is your <strong>last resort</strong> for accessing your protected
          data if you lose all your devices. Write it down and store it securely.
        </Text>
        <Text size="sm" mt="xs" fw={500}>
          This phrase will only be shown ONCE.
        </Text>
      </Alert>

      {/* Recovery phrase display */}
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={500} size="sm">Your Recovery Phrase</Text>
          <Group gap="xs">
            <Tooltip label={showPhrase ? 'Hide phrase' : 'Show phrase'}>
              <ActionIcon
                variant="subtle"
                onClick={() => setShowPhrase(!showPhrase)}
              >
                {showPhrase ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              </ActionIcon>
            </Tooltip>
            <CopyButton value={phraseText}>
              {({ copied, copy }: { copied: boolean; copy: () => void }) => (
                <Tooltip label={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? 'green' : 'gray'}
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Group>

        {showPhrase ? (
          <SimpleGrid cols={4} spacing="xs">
            {phrase.map((word, index) => (
              <Paper key={index} p="xs" bg="gray.0" radius="sm">
                <Group gap={4}>
                  <Text size="xs" c="dimmed" w={16}>
                    {index + 1}.
                  </Text>
                  <Text size="sm" fw={500}>
                    {word}
                  </Text>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        ) : (
          <Box
            p="xl"
            bg="gray.1"
            style={{ borderRadius: 'var(--mantine-radius-sm)', textAlign: 'center' }}
          >
            <Text c="dimmed" size="sm">
              Click the eye icon to reveal your recovery phrase
            </Text>
          </Box>
        )}
      </Paper>

      {/* Confirmation checkbox */}
      <Checkbox
        label="I have written down this recovery phrase and stored it securely"
        checked={confirmed}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmed(e.currentTarget.checked)}
        description="You will not be able to see this phrase again"
      />

      {/* Skipping warning */}
      <Alert color="gray" variant="light" icon={<IconKey size={16} />}>
        <Text size="sm">
          <strong>Skipping this step?</strong> Without a recovery phrase, you cannot recover
          your protected data if you lose access to all your registered devices.
        </Text>
      </Alert>

      {/* Action buttons */}
      <Group justify="space-between" mt="md">
        <Button variant="subtle" onClick={onSkip} disabled={loading}>
          Skip This Step
        </Button>
        <Button
          onClick={handleComplete}
          loading={loading}
          disabled={!confirmed}
          leftSection={<IconCheck size={18} />}
        >
          I Saved My Phrase
        </Button>
      </Group>
    </Stack>
  );
};
